import { createFileRoute } from '@tanstack/react-router'
import { AuthContainer } from '@/components/auth/AuthContainer'
import { CheckCircle2, AlertCircle, Shield, Clock } from 'lucide-react'

/**
 * Login Route with Logout Reason Support
 *
 * Query Parameters:
 * - reason: Reason for being on the login page (logged_out, session_expired, security, etc.)
 *
 * Examples:
 * - /login
 * - /login?reason=logged_out
 * - /login?reason=session_expired
 */

interface LoginSearch {
  reason?: 'logged_out' | 'session_expired' | 'security' | 'admin_action' | 'user_action' | 'logout_error'
}

export const Route = createFileRoute('/_unauth/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    return {
      reason: (search.reason === 'logged_out' ||
               search.reason === 'session_expired' ||
               search.reason === 'security' ||
               search.reason === 'admin_action' ||
               search.reason === 'user_action' ||
               search.reason === 'logout_error')
        ? search.reason
        : undefined
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const { reason } = Route.useSearch()

  // Define message configuration for each logout reason
  const logoutMessages = {
    logged_out: {
      icon: CheckCircle2,
      iconColor: 'text-green-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      title: 'Você saiu com sucesso',
      message: 'Entre novamente para continuar usando o sistema.'
    },
    session_expired: {
      icon: Clock,
      iconColor: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      title: 'Sessão expirada',
      message: 'Sua sessão expirou por inatividade. Por favor, faça login novamente.'
    },
    security: {
      icon: Shield,
      iconColor: 'text-red-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      title: 'Logout de segurança',
      message: 'Você foi desconectado por razões de segurança. Por favor, faça login novamente.'
    },
    admin_action: {
      icon: AlertCircle,
      iconColor: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
      title: 'Conta desconectada',
      message: 'Sua sessão foi encerrada por um administrador. Entre em contato com o suporte se necessário.'
    },
    user_action: {
      icon: CheckCircle2,
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      title: 'Você saiu com sucesso',
      message: 'Entre novamente quando quiser continuar.'
    },
    logout_error: {
      icon: AlertCircle,
      iconColor: 'text-red-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      title: 'Erro ao sair',
      message: 'Houve um erro durante o logout, mas sua sessão foi limpa. Você pode fazer login novamente.'
    }
  }

  const messageConfig = reason ? logoutMessages[reason] : null
  const MessageIcon = messageConfig?.icon

  return (
    <div className="space-y-4">
      {/* Logout Reason Banner */}
      {messageConfig && MessageIcon && (
        <div
          className={`${messageConfig.bgColor} ${messageConfig.borderColor} border rounded-monumenta p-4 animate-fade-in`}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start space-x-3">
            <MessageIcon className={`h-5 w-5 mt-0.5 ${messageConfig.iconColor} flex-shrink-0`} />
            <div className="flex-1">
              <h3 className={`font-display font-semibold text-sm ${messageConfig.iconColor}`}>
                {messageConfig.title}
              </h3>
              <p className="text-gray-300 text-sm mt-1 font-body">
                {messageConfig.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Auth Container */}
      <AuthContainer initialState="signIn" />
    </div>
  )
}
